import cn from 'classnames'
import { useCallback, useMemo } from 'react'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { useFormField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import { ISobAnalyticsReportJoined } from 'partner360/models/report/sobAnalyticsReports/ISobAnalyticsReportJoined'

import { getUniqueValues } from '../../../../helpers/getUniqueValues'
import { AcknowledgedStatus } from '../../../../models'
import { getExcludedField } from '../../../helpers/filters/getExcludedField'
import { isShowExcluded } from '../../../helpers/filters/isShowExcluded'
import { isShowObligatoryFillInAll } from '../../../helpers/filters/isShowObligatoryFillInAll'
import { isShowPlanned } from '../../../helpers/filters/isShowPlanned'
import { useReportViewSelector } from '../../../hooks/useReportViewSelector'
import { TRANSLATION_KEYS } from '../../../translations'

import { IN_REPORT_OPTIONS } from './configs'
import { ObligatoryFillIn } from './models'

import styles from './SobAnalyticsInReportToggle.module.scss'

interface IInReportToggleProps {
  className?: string
  obligatoryFillInField: string
  defaultObligatoryFillInValue: ObligatoryFillIn
  selectedFYear: number
  formKey: string
  joinedData: ISobAnalyticsReportJoined[]
  onChange?: (value: ObligatoryFillIn) => void
  acknowledgedStatus: AcknowledgedStatus
}

export const SobAnalyticsInReportToggle = ({
  className,
  joinedData,
  defaultObligatoryFillInValue,
  formKey,
  selectedFYear,
  obligatoryFillInField,
  onChange,
  acknowledgedStatus,
}: IInReportToggleProps) => {
  const { reportViewPeriod } = useReportViewSelector()

  const { value, handleChange } = useFormField<ObligatoryFillIn>({
    defaultValue: defaultObligatoryFillInValue,
    field: obligatoryFillInField,
    formKey: formKey,
  })

  const selectedIndex = useMemo(() => {
    return IN_REPORT_OPTIONS.findIndex((el) => el.key === value)
  }, [value])

  const filteredJoinedData = useMemo(() => {
    return joinedData.filter((it) => it.FYear === selectedFYear)
  }, [joinedData, selectedFYear])

  const filteredAll = useMemo(() => {
    return filteredJoinedData.filter((it) => isShowObligatoryFillInAll(acknowledgedStatus, it))
  }, [filteredJoinedData, acknowledgedStatus])

  const excludedField = useMemo(() => {
    return getExcludedField({
      selectedPeriod: reportViewPeriod,
    })
  }, [reportViewPeriod])

  const filteredPlanned = useMemo(() => {
    return filteredJoinedData.filter((it) =>
      isShowPlanned({
        acknowledgedStatus,
        excludedField,
        el: it,
      })
    )
  }, [filteredJoinedData, acknowledgedStatus, excludedField])

  const filteredExcluded = useMemo(() => {
    return filteredJoinedData.filter((it) =>
      isShowExcluded({
        acknowledgedStatus,
        excludedField: excludedField,
        el: it,
      })
    )
  }, [filteredJoinedData, acknowledgedStatus, excludedField])

  const uniqAll = useMemo(() => {
    return getUniqueValues(filteredAll, 'PartnerId')
  }, [filteredAll])

  const uniqExcluded = useMemo(() => {
    return getUniqueValues(filteredExcluded, 'PartnerId')
  }, [filteredExcluded])

  const uniqPlanned = useMemo(() => {
    return getUniqueValues(filteredPlanned, 'PartnerId')
  }, [filteredPlanned])

  const getCount = useCallback(
    (value: ObligatoryFillIn) => {
      if (value === ObligatoryFillIn.Excluded) {
        return uniqExcluded.length
      }

      if (value === ObligatoryFillIn.Planned) {
        return uniqPlanned.length
      }

      return uniqAll.length
    },
    [uniqPlanned, uniqExcluded, uniqAll]
  )

  const onHandleChange = useCallback(
    (index: number) => {
      const item = IN_REPORT_OPTIONS[index].key
      if (onChange) {
        onChange(item)
      }
      handleChange(item)
    },
    [handleChange, onChange]
  )

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.label}>
        <Translation text={TRANSLATION_KEYS.filters.obligatoryFieldLabel} />
      </div>

      <SegmentedControl
        selectedItem={selectedIndex}
        backgroudn="bg-default"
        variant="light"
        onItemSelect={onHandleChange}
      >
        {IN_REPORT_OPTIONS.map((item) => (
          <SegmentedControl.Item key={item.key}>
            <Tooltip
              tipContent={() => <Translation text={item.tooltip(reportViewPeriod?.Quarters)} />}
            >
              <Translation
                span
                text={item.label}
                replaceParams={[{ key: '{count}', value: String(getCount(item.key)) }]}
              />
            </Tooltip>
          </SegmentedControl.Item>
        ))}
      </SegmentedControl>
    </div>
  )
}
